import { type ComponentPropsWithoutRef } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useGetQuizsetSummary } from '@/api/gen/quizset/quizset.gen';
import { useOpenWindow, useViewport } from '@/hooks';
import { BookCoverFillIcon, LockFillIcon } from '@/icons';
import { SchoolInfoChip } from '@/shared/components/Chip/QuizInfoChip';
import { SubjectChip } from '@/shared/components/SubjectChip';
import { Button } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { SettingChangeButton } from '@/views/build/setting/SettingChangeButton';
export const NextQuizset = ({
  quizsetId,
  onChangeButtonClick,
  enableChange = false
}: {
  quizsetId?: string;
  onChangeButtonClick?: () => void;
  enableChange?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    isMobile
  } = useViewport();
  const openWindow = useOpenWindow();
  const {
    data,
    error
  } = useGetQuizsetSummary({
    quizsetId: quizsetId as string
  }, {
    query: {
      enabled: !!quizsetId,
      throwOnError: false
    }
  });
  if (error?.httpStatus === 404) {
    return <Container className="bg-neutral">
        <div className="flex items-center gap-sm">
          <div className="flex h-[52px] w-[79px] items-center justify-center rounded-sm bg-alternative">
            <BookCoverFillIcon size="xl" fill={ICON_COLOR.disabled} />
          </div>
          <p className="text-body-sm-semibold text-assistive">
            {t('common:nextQuiz.cantLoad')}
          </p>
        </div>
      </Container>;
  }
  if (!data) return null;
  return <div className="relative" data-sentry-component="NextQuizset" data-sentry-source-file="NextQuizset.tsx">
      <Container className={cn('min-h-[82px] max-mobile-lg:cursor-pointer justify-between max-w-full', data.data.isLocked && 'pointer-events-none opacity-20')} onClick={() => {
      if (isMobile) {
        logger.track('previewNextQuizset', {
          from: 'card'
        });
        openWindow(`/quiz/${quizsetId}`);
      }
    }} data-sentry-element="Container" data-sentry-source-file="NextQuizset.tsx">
        <div className="flex min-w-0 grow items-center gap-sm">
          {data.data.templateImageUrl && <Image src={toHttpsImageUrl(data.data.templateImageUrl)} alt={data.data.title} className="h-[46px] w-[70px] flex-shrink-0 rounded-sm" width={700} height={460} />}
          <div className="flex min-w-0 flex-1 flex-col gap-2xs overflow-hidden">
            <div className="flex min-w-0 gap-3xs overflow-hidden">
              {data.data.categoryInfo && <SchoolInfoChip className="min-w-0 flex-shrink truncate text-caption-md-semibold [&>span]:truncate" schoolInfo={data.data.categoryInfo} />}
              {data.data.categoryInfo && <SubjectChip {...data.data.categoryInfo} className="min-w-0 flex-shrink truncate text-caption-md-semibold [&>span]:truncate" />}
            </div>
            <span className="inline-block min-w-0 truncate text-body-md-bold">
              {data.data.title}
            </span>
          </div>
        </div>

        <Button variant="outlined" intent="neutral" size="xs" className="hidden shrink-0 mobile-lg:flex" asChild={true} onClick={() => {
        logger.track('previewNextQuizset', {
          from: 'card'
        });
      }} data-sentry-element="Button" data-sentry-source-file="NextQuizset.tsx">
          <Link href={`/quiz/${quizsetId}`} target="_blank" data-sentry-element="Link" data-sentry-source-file="NextQuizset.tsx">
            {t('common:preview')}
          </Link>
        </Button>
        {enableChange && <SettingChangeButton onClick={e => {
        onChangeButtonClick?.();
        e.stopPropagation();
      }} />}
      </Container>

      {data.data.isLocked && <div className="absolute left-0 top-0 flex size-full items-center justify-center gap-[10px]">
          <LockFillIcon size="md" fill={ICON_COLOR.alternative} />
          <span className="text-body-lg-bold text-alternative">
            {t('common:nextQuiz.lockQuiz')}
          </span>
        </div>}
    </div>;
};
const toHttpsImageUrl = (url: string) => url.replace(/^http:\/\//i, 'https://');
const Container = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('py-md px-lg flex border border-alternative rounded-md items-center gap-sm', className)} {...props} data-sentry-component="Container" data-sentry-source-file="NextQuizset.tsx">
      {children}
    </div>;
};